import React, { Fragment, ReactNode } from 'react';

/**
 * Stands in for a rendered node while a sentence is being translated, so the
 * node can be spliced back where its placeholder was. A control character,
 * because it cannot occur in a name or in a translation.
 *
 * Passing a sentinel and splitting on it is the only way to read a template
 * back: i18next substitutes `''` for a placeholder given no value, so calling
 * `t` with no options destroys the very thing being looked for.
 */
export const TRANSLATION_SLOT = '\u0000';

/**
 * A translated sentence with a React node standing where its placeholder was —
 * for text that has to be built from a key (word order and punctuation are a
 * translator's call) but styled or made interactive in the middle.
 *
 * Total by construction, because the string comes from a translator and no
 * amount of review reaches every language:
 * - the placeholder is missing → the node alone. It is the half that carries
 *   the information — a layer's name, a list of them — and the frame around it
 *   is context. Keeping the frame instead reads "…does not reach : those layers
 *   keep their own", which is worse than terse.
 * - the placeholder is repeated → the node at each occurrence, as written.
 */
function spliceIntoTranslation(sentence: string, node: ReactNode): ReactNode {
  const parts = sentence.split(TRANSLATION_SLOT);

  if (parts.length === 1) {
    return node;
  }

  return parts.map((part, index) => (
    // eslint-disable-next-line react/no-array-index-key
    <Fragment key={index}>
      {index > 0 && node}
      {part}
    </Fragment>
  ));
}

export default spliceIntoTranslation;
